import { useMutation } from "@apollo/client/react";
import { Box, Icon, IconicButton, TruncatedText } from "@nulogy/components";
import { GridBox7, StyledInput, StyledText } from "components/common/EditableListStyledComponents";
import FieldRow from "components/common/FieldRow";
import Form from "components/controls/Form";
import { UpdateLine } from "hooks/apollo/line/line.gql";
import React, { useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import Num from "utils/Num";
import LineStatusIndicator from "./LineStatusIndicator";
import LineStatusSelect from "./LineStatusSelect";

export default function Line({ line, onDelete, index }) {
  const [editing, setEditing] = useState(false);
  const [updateLine] = useMutation(UpdateLine, { onCompleted: () => setEditing(false) });

  const { id, name, lineType, efficiency, active } = line;

  const handleCancel = (onReset) => {
    onReset();
    setEditing(false);
  };

  const handleSave = (updatedLine) => {
    const efficiencyDecimal = updatedLine.efficiency === "" ? null : Num.percentToDecimal(updatedLine.efficiency);

    return updateLine({
      variables: {
        active: updatedLine.active,
        efficiency: efficiencyDecimal,
        id: updatedLine.id,
        lineType: updatedLine.lineType,
        name: updatedLine.name,
      },
    });
  };

  return (
    <Draggable draggableId={line.id} index={index} key={line.id}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          {!editing && (
            <GridBox7 className={`spec-line-${id}`} data-testid="line">
              <div {...provided.dragHandleProps} className={`spec-line-drag-${id} spec-line-drag`}>
                <StyledText>
                  <Icon color="darkBlue" icon="drag" />
                </StyledText>
              </div>
              <StyledText className="spec-line-name" data-testid="line-name">
                <TruncatedText fullWidth>{name}</TruncatedText>
              </StyledText>
              <StyledText data-testid="line-type">
                <TruncatedText fullWidth>{lineType}</TruncatedText>
              </StyledText>
              <StyledText data-testid="line-efficiency">{Num.decimalToPercent(efficiency)}</StyledText>
              <StyledText data-testid="line-active">
                <LineStatusIndicator value={active} />
              </StyledText>
              <IconicButton icon="edit" onClick={() => setEditing(true)} type="button">
                Edit
              </IconicButton>
              <IconicButton icon="delete" onClick={() => onDelete(line)} type="button">
                Delete
              </IconicButton>
            </GridBox7>
          )}
          {editing && (
            <Form
              initialValues={{ active, efficiency: Num.decimalToPercent(efficiency), id, lineType, name }}
              onSave={handleSave}
            >
              {({ dirty, handleReset }) => (
                <FieldRow data-testid="line-row-editing" marginBottom="none">
                  <GridBox7>
                    <div {...provided.dragHandleProps}>
                      <StyledText>
                        <Icon color="darkBlue" icon="drag" />
                      </StyledText>
                    </div>
                    <StyledInput field="name" placeholder="Name" />
                    <StyledInput field="lineType" placeholder="Type" />
                    <StyledInput
                      field="efficiency"
                      placeholder="Efficiency"
                      step="any"
                      style={{ width: "112px" }}
                      type="number"
                    />
                    <LineStatusSelect />
                    <Box paddingTop="x1">
                      <IconicButton disabled={!dirty} icon="check" type="submit">
                        Save
                      </IconicButton>
                    </Box>
                    <Box paddingTop="x1">
                      <IconicButton icon="close" onClick={() => handleCancel(handleReset)} type="button">
                        Cancel
                      </IconicButton>
                    </Box>
                  </GridBox7>
                </FieldRow>
              )}
            </Form>
          )}
        </div>
      )}
    </Draggable>
  );
}
